import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import { getApi, deleteApi, postApi } from '../../Services/apiService';
import { toast } from 'react-toastify';
import {
  getPropertyListingStatusLabel,
  getPropertyListingStatusSlug,
} from '../../constants/propertyListingStatus';
import { getPropertyLimitMessage, hasReachedPropertyLimit } from '../../Utils/propertyLimit';

const MyProperties = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // API state
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [connectedAccount, setConnectedAccount] = useState(null);
  // Unfiltered property count - `pagination.totalItems` follows the search box,
  // so it cannot be used to check the plan limit.
  const [totalPropertyCount, setTotalPropertyCount] = useState(null);
  const [addButtonTooltipVisible, setAddButtonTooltipVisible] = useState(false);
  const addButtonTooltipHideTimeoutRef = useRef(null);
  const loadMoreRef = useRef(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    totalItems: 0
  });

  // Transform API response to component format
  const transformProperty = (apiProperty) => {
    // Get first property image or use placeholder
    const firstImage = apiProperty.property_images && apiProperty.property_images.length > 0
      ? apiProperty.property_images[0].image_url
      : 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=400';
    
    // Format address
    const address = apiProperty.address || '';
    const unitNumber = apiProperty.unit_number ? `, ${apiProperty.unit_number}` : '';
    const fullAddress = `${address}${unitNumber}`;
    
    // Format city and state
    const cityState = apiProperty.city && apiProperty.state 
      ? `${apiProperty.city}, ${apiProperty.state}`
      : apiProperty.city || apiProperty.state || '';
    
    // Format price
    const price = apiProperty.monthly_rent 
      ? `$${apiProperty.monthly_rent.toLocaleString()}/mo`
      : 'N/A';
    
    // Format bedrooms
    const bedrooms = apiProperty.bedrooms 
      ? `${apiProperty.bedrooms}${apiProperty.bedrooms === 1 ? 'BHK' : 'BHK'}`
      : '';
    
    // Format square feet
    const sqft = apiProperty.square_feet 
      ? `${apiProperty.square_feet.toLocaleString()} sq.ft`
      : '';
    
    // Format listed date
    const listedDate = apiProperty.listed_at 
      ? new Date(Number(apiProperty.listed_at)).toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          year: 'numeric' 
        })
      : '';
    
    // Use property_code from API, fallback to first 5 characters of ID if not available
    const code = apiProperty.property_code || (apiProperty.id ? apiProperty.id.substring(0, 5) : '');

    const rawStatus = apiProperty.status;

    return {
      id: apiProperty.id,
      image: firstImage,
      address: fullAddress,
      code: code,
      city: cityState,
      price: price,
      bedrooms: bedrooms,
      sqft: sqft,
      status: getPropertyListingStatusLabel(rawStatus),
      statusSlug: getPropertyListingStatusSlug(rawStatus),
      applicants: Number(apiProperty.applicants_count) || 0,
      listedDate: listedDate,
      llcName: apiProperty.llc_name || '',
      llcLogo: apiProperty.llc_logo || '',
    };
  };

  // Fetch properties from API
  const fetchProperties = async (page = 1, append = false, search = '') => {
    const limit = pagination.pageSize;
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (search && search.trim()) {
        params.append('search', search.trim());
      }

      const response = await getApi(`/property/my-properties?${params.toString()}`);

      if (response?.statusCode === 200 && response?.data) {
        const transformedProperties = response.data.data.map(transformProperty);
        setProperties((prev) => (append ? [...prev, ...transformedProperties] : transformedProperties));
        setPagination({
          currentPage: response.data.currentPage || page,
          pageSize: response.data.pageSize || limit,
          totalPages: response.data.totalPages || 1,
          totalItems: response.data.totalItems || 0
        });
        if (!search || !search.trim()) {
          setTotalPropertyCount(Number(response.data.totalItems) || 0);
        }
      } else if (!append) {
        setProperties([]);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      if (!append) setProperties([]);
    } finally {
      if (append) {
        setIsLoadingMore(false);
      } else {
        setIsLoading(false);
      }
    }
  };

  // Debounced search query state
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const searchTimeoutRef = useRef(null);

  const hasMore = pagination.currentPage < pagination.totalPages;

  // Used after a delete: the list refresh may be filtered by the search box, so
  // the plan-limit count has to be read back on its own.
  const refreshPropertyCount = useCallback(async () => {
    try {
      const response = await getApi('/property/my-properties?page=1&limit=1');
      if (response?.statusCode === 200 && response?.data) {
        setTotalPropertyCount(Number(response.data.totalItems) || 0);
      }
    } catch (error) {
      console.error('Error fetching property count:', error);
    }
  }, []);

  const loadMore = useCallback(() => {
    if (isLoading || isLoadingMore || !hasMore) return;
    fetchProperties(pagination.currentPage + 1, true, debouncedSearchQuery);
  }, [isLoading, isLoadingMore, hasMore, pagination.currentPage, debouncedSearchQuery]);
  
  // Debounce search query
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Check if landlord has connected Stripe account
  useEffect(() => {
    const fetchConnectedAccount = async () => {
      try {
        const response = await getApi('/landlord/check-connected-account');
        if (response?.statusCode === 200 && response?.data) {
          setConnectedAccount(response.data);
        } else {
          setConnectedAccount({ is_connected: 0 });
        }
      } catch (error) {
        console.error('Error fetching connected account:', error);
        setConnectedAccount({ is_connected: 0 });
      }
    };
    fetchConnectedAccount();
  }, []);

  const showAddButtonTooltip = () => {
    if (addButtonTooltipHideTimeoutRef.current) {
      clearTimeout(addButtonTooltipHideTimeoutRef.current);
      addButtonTooltipHideTimeoutRef.current = null;
    }
    setAddButtonTooltipVisible(true);
  };

  const hideAddButtonTooltip = () => {
    addButtonTooltipHideTimeoutRef.current = setTimeout(() => {
      setAddButtonTooltipVisible(false);
      addButtonTooltipHideTimeoutRef.current = null;
    }, 250);
  };

  useEffect(() => {
    return () => {
      if (addButtonTooltipHideTimeoutRef.current) {
        clearTimeout(addButtonTooltipHideTimeoutRef.current);
      }
    };
  }, []);

  // Fetch first page when search changes
  useEffect(() => {
    fetchProperties(1, false, debouncedSearchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery]);

  // Infinite scroll: load next page when sentinel enters viewport
  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { rootMargin: '200px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore, properties.length]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuId !== null) {
        const menuElement = document.querySelector(`.property-card-menu-container[data-property-id="${openMenuId}"]`);
        if (menuElement && !menuElement.contains(event.target)) {
          setOpenMenuId(null);
        }
      }
    };

    if (openMenuId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuId]);

  const handleMenuToggle = (propertyId, event) => {
    event.preventDefault();
    event.stopPropagation();
    setOpenMenuId(openMenuId === propertyId ? null : propertyId);
  };

  const handleMenuAction = async (action, propertyId, event) => {
    event.stopPropagation();
    setOpenMenuId(null);
    
    if (action === 'delete') {
      setPropertyToDelete(propertyId);
      setDeleteModal(true);
    } else if (action === 'view') {
      navigate(`/landlord/properties/${propertyId}`);
    } else if (action === 'copy') {
      try {
        const response = await postApi(`/property/${propertyId}/share`, {});
        const url = response?.data?.url;
        if (!url) {
          toast.error('Could not get share link');
          return;
        }

        // Prefer modern clipboard API when available and permitted
        if (navigator && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
          await navigator.clipboard.writeText(url);
          toast.success('Link copied to clipboard!');
          return;
        }

        // Fallback for environments where navigator.clipboard is not available (e.g. some HTTP origins)
        const textarea = document.createElement('textarea');
        textarea.value = url;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          const successful = document.execCommand('copy');
          if (successful) {
            toast.success('Link copied to clipboard!');
          } else {
            toast.error('Unable to copy link. Please copy it manually.');
          }
        } finally {
          document.body.removeChild(textarea);
        }
      } catch (error) {
        console.error('Error generating share link:', error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to generate share link. Please try again.';
        toast.error(errorMessage);
      }
    } else if (action === 'edit') {
      // Navigate to edit property page
      navigate(`/landlord/properties/edit/${propertyId}`);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!propertyToDelete || isDeleting) return;
    
    setIsDeleting(true);
    try {
      const response = await deleteApi('/property/delete', {
        property_id: propertyToDelete
      });
      
      if (response?.statusCode === 200) {
        toast.success(response?.message || 'Property deleted successfully');
        // Refresh properties list
        fetchProperties(1, false, debouncedSearchQuery);
        if (debouncedSearchQuery && debouncedSearchQuery.trim()) {
          refreshPropertyCount();
        }
        setDeleteModal(false);
        setPropertyToDelete(null);
      } else {
        toast.error(response?.message || 'Failed to delete property');
      }
    } catch (error) {
      console.error('Error deleting property:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete property. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal(false);
    setPropertyToDelete(null);
  };

  // Add Property is blocked by two things: a missing payout account, and the
  // plan's property limit. The payout account comes first - without it nothing
  // can be listed at all.
  const isAccountConnected = connectedAccount?.is_connected === 1;
  const isAccountBlocked = connectedAccount != null && !isAccountConnected;
  const isLimitReached =
    totalPropertyCount !== null && hasReachedPropertyLimit(totalPropertyCount);
  const isAddPropertyBlocked = !isAccountConnected || isLimitReached;
  const addPropertyBlockedMessage = isAccountBlocked
    ? 'You have not connected your account. Connect your payout method in Profile → Subscription to add properties.'
    : isLimitReached
      ? getPropertyLimitMessage()
      : '';

  const handleAddProperty = () => {
    if (isAddPropertyBlocked) return;
    navigate('/landlord/properties/add');
  };

  const PropertyCardSkeleton = () => (
    <div className="property-card property-card-skeleton" aria-hidden="true">
      <div className="property-card-image-container">
        <div className="property-card-image-shimmer" />
      </div>
      <div className="property-card-content">
        <div className="property-card-skeleton-line property-card-skeleton-line-wide" />
        <div className="property-card-skeleton-line property-card-skeleton-line-medium" />
        <div className="property-card-skeleton-line property-card-skeleton-line-short" />
      </div>
    </div>
  );

  const PropertyCard = ({ property }) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [llcLogoError, setLlcLogoError] = useState(false);
    const imageRef = useRef(null);
    const isMenuOpen = openMenuId === property.id;

    useEffect(() => {
      setImageLoaded(false);
      setImageError(false);
      setLlcLogoError(false);
      const img = imageRef.current;
      if (img?.complete && img.naturalWidth > 0) {
        setImageLoaded(true);
      }
    }, [property.image]);

    return (
      <div className="property-card">
        <div className="property-card-image-container">
          {imageError ? (
            <div className="property-card-image-placeholder" />
          ) : (
            <>
              {!imageLoaded && <div className="property-card-image-shimmer" aria-hidden="true" />}
              <img
                ref={imageRef}
                src={property.image}
                alt={property.address}
                className={`property-card-image${imageLoaded ? ' property-card-image-loaded' : ' property-card-image-loading'}`}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
              />
            </>
          )}
          {(property.llcLogo || property.llcName) && (
            <div className="property-card-llc" title={property.llcName || undefined}>
              {property.llcLogo && !llcLogoError ? (
                <img
                  src={property.llcLogo}
                  alt={property.llcName ? `${property.llcName} logo` : 'Business logo'}
                  className="property-card-llc-logo"
                  onError={() => setLlcLogoError(true)}
                />
              ) : (
                <span className="property-card-llc-fallback" aria-hidden="true">
                  {(property.llcName || '?').trim().charAt(0).toUpperCase()}
                </span>
              )}
              {property.llcName && (
                <span className="property-card-llc-name">{property.llcName}</span>
              )}
            </div>
          )}
          <div 
            className="property-card-menu-container" 
            data-property-id={property.id}
          >
            <button 
              className="property-card-more-button"
              onClick={(e) => handleMenuToggle(property.id, e)}
            >
              <img 
                src="/assets/images/verticalThreeDots.png" 
                alt="More options" 
                className="property-card-more-icon"
              />
            </button>
            {isMenuOpen && (
              <div className="property-card-menu">
                <button 
                  className="property-card-menu-item"
                  onClick={(e) => handleMenuAction('view', property.id, e)}
                >
                  <img 
                    src="/assets/images/Eye.svg" 
                    alt="View" 
                    className="property-card-menu-icon"
                  />
                  <span>View</span>
                </button>
                <button 
                  className="property-card-menu-item"
                  onClick={(e) => handleMenuAction('edit', property.id, e)}
                >
                  <img 
                    src="/assets/images/EditPencilIcon.svg" 
                    alt="Edit" 
                    className="property-card-menu-icon"
                  />
                  <span>Edit</span>
                </button>
                <button 
                  className="property-card-menu-item"
                  onClick={(e) => handleMenuAction('copy', property.id, e)}
                >
                  <img 
                    src="/assets/images/CopyIcon.svg" 
                    alt="Copy link" 
                    className="property-card-menu-icon"
                  />
                  <span>Copy link</span>
                </button>
                <div className="property-card-menu-divider"></div>
                <button 
                  className="property-card-menu-item property-card-menu-item-delete"
                  onClick={(e) => handleMenuAction('delete', property.id, e)}
                >
                  <img 
                    src="/assets/images/deleteIcon.svg" 
                    alt="Delete" 
                    className="property-card-menu-icon"
                  />
                  <span>Delete</span>
                </button>
              </div>
            )}
          </div>
        </div>
        <div 
          className="property-card-content"
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/landlord/properties/${property.id}`)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(`/landlord/properties/${property.id}`);
            }
          }}
        >
          <div className="property-card-top">
            <div className="property-card-header">
              <div className="property-card-address-section">
                <div className="property-card-address" title={property.address}>{property.address}</div>
                <div className="property-card-code">Code: {property.code}</div>
              </div>
              <div className={`property-card-status property-card-status-${property.statusSlug}`}>
                {property.status}
              </div>
            </div>
            <div className="property-card-location">{property.city}</div>
          </div>
          <div className='property-card-location-flex'>
          <div className="property-card-price">
            {property.price.endsWith('/mo') ? (
              <>
                {property.price.replace(/\/mo$/, '')}
                <span className="property-card-price-suffix">/mo</span>
              </>
            ) : (
              property.price
            )}
          </div>
          <div className="property-card-details">
            {property.bedrooms} {property.sqft}
          </div>
          </div>
          <div className="property-card-footer">
            <div className="property-card-metric">
              <img 
                src="/assets/images/users-profiles-01.png" 
                alt="Applicants" 
                className="property-card-metric-icon"
              />
              <span>{property.applicants} Applicant{property.applicants !== 1 ? 's' : ''}</span>
            </div>
            <div className="property-card-metric">
              <img 
                src="/assets/images/calendar-01.png" 
                alt="Calendar" 
                className="property-card-metric-icon"
              />
              <span>Listed {property.listedDate}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="properties-page">
      <div className="properties-actions-bar">
        <div className="properties-search-wrapper">
          <img 
            src="/assets/images/SearchIcon.svg" 
            alt="Search" 
            className="properties-search-icon"
          />
          <input
            type="text"
            className="properties-search-input"
            placeholder="Search Properties"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div
          className="properties-add-button-wrapper"
          onMouseEnter={showAddButtonTooltip}
          onMouseLeave={hideAddButtonTooltip}
        >
          <button 
            className="properties-add-button"
            onClick={handleAddProperty}
            disabled={isAddPropertyBlocked}
          >
            <img 
              src="/assets/images/plusIcon.png" 
              alt="Add" 
              className="properties-add-icon"
            />
            Add Property
          </button>
          {addPropertyBlockedMessage && (
            <div
              className={`properties-add-button-tooltip ${addButtonTooltipVisible ? 'properties-add-button-tooltip-visible' : ''}`}
              onMouseEnter={showAddButtonTooltip}
              onMouseLeave={hideAddButtonTooltip}
            >
              <p className="properties-add-button-tooltip-text">
                {addPropertyBlockedMessage}
              </p>
              <button
                type="button"
                className="properties-add-button-tooltip-link"
                onClick={() => navigate('/landlord/settings/subscription')}
              >
                {isAccountBlocked ? 'Go to Subscription' : 'Upgrade Plan'}
              </button>
            </div>
          )}
        </div>
      </div>
      {isLoading ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px',
          fontSize: '18px',
          color: '#666'
        }}>
          Loading properties...
        </div>
      ) : properties.length === 0 ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px',
          fontSize: '18px',
          color: '#666'
        }}>
          No properties found
        </div>
      ) : (
        <div className="properties-grid">
          {properties.map((property) => (
            <PropertyCard key={property.id} property={property} />
          ))}
          {isLoadingMore && (
            <>
              <PropertyCardSkeleton />
              <PropertyCardSkeleton />
              <PropertyCardSkeleton />
            </>
          )}
          {hasMore && <div ref={loadMoreRef} className="properties-load-more-sentinel" aria-hidden="true" />}
        </div>
      )}

      <Modal
        isOpen={deleteModal}
        onRequestClose={isDeleting ? undefined : handleDeleteCancel}
        contentLabel="Delete Property Modal"
        shouldCloseOnOverlayClick={!isDeleting}
        ariaHideApp={false}
        className="delete-modal"
        overlayClassName="delete-modal-overlay"
      >
        <button 
          className="delete-modal-close" 
          onClick={handleDeleteCancel}
          disabled={isDeleting}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="delete-modal-icon">
          <div className="delete-modal-icon-wrapper">
            <img 
              src="/assets/images/deleteIcon.svg" 
              alt="Delete" 
              className="delete-modal-icon-img"
            />
          </div>
        </div>
        <h2 className="delete-modal-title">Delete</h2>
        <p className="delete-modal-message">Are you sure you want to delete this property?</p>
        <div className="delete-modal-buttons">
          <button 
            className="delete-modal-cancel" 
            onClick={handleDeleteCancel}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button 
            className="delete-modal-confirm" 
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default MyProperties;
